import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  Check,
  ChevronRight,
  Loader,
  Shield,
  MessageSquare,
  ArrowRight,
  Key,
  User,
  Wrench,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { azureApi, type AzureDeployment } from "@/api/azure";
import { queryKeys } from "@/lib/query-keys";
import type { CreateAgentInput } from "@/api/agents";
import { PHASES, VM_SIZES, REGIONS, fillTemplate, type Phase } from "./data";
import { Pill } from "./steps";

type LogLine = { ts: string; msg: string; tone?: "ok" | "err" };

function formatTs(date: Date): string {
  return date.toISOString().substring(14, 19);
}

function LogStream({ lines }: { lines: LogLine[] }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [lines.length]);
  return (
    <div
      ref={ref}
      className="h-[180px] overflow-y-auto rounded-[10px] border p-3 font-mono text-[11.5px] leading-relaxed"
      style={{ background: "#13120f", borderColor: "#2a2823", color: "#c9c6bf" }}
    >
      {lines.map((l, i) => (
        <div
          key={i}
          className="flex gap-2.5"
          style={{ opacity: i === lines.length - 1 ? 1 : 0.65 }}
        >
          <span style={{ color: "#6d6a62" }} className="shrink-0">
            {l.ts}
          </span>
          <span
            style={{
              color: l.tone === "err" ? "#f28b82" : l.tone === "ok" ? "#81c995" : "#c9c6bf",
            }}
          >
            {l.msg}
          </span>
        </div>
      ))}
      <span
        className="inline-block align-text-bottom"
        style={{
          width: 7,
          height: 13,
          background: "#81c995",
          animation: "blink 1s linear infinite",
        }}
      />
    </div>
  );
}

function PhaseRow({
  phase,
  state,
  progress,
  expanded,
  onToggle,
  currentStepIdx,
  vmLabel,
  regionLabel,
}: {
  phase: Phase;
  state: "pending" | "active" | "done";
  progress: number;
  expanded: boolean;
  onToggle: () => void;
  currentStepIdx: number;
  vmLabel: string;
  regionLabel: string;
}) {
  const PhaseIcon: LucideIcon = phase.icon;
  return (
    <div
      className={`overflow-hidden rounded-xl border transition-all ${
        state === "active"
          ? "border-accent/40 bg-accent/[0.03] shadow-[0_0_0_3px_rgba(16,163,127,0.08)]"
          : "border-border bg-surface"
      }`}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3.5 border-0 bg-transparent px-4 py-3.5 text-left"
      >
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${
            state === "done"
              ? "bg-accent/15 text-accent"
              : state === "active"
                ? "bg-accent/10 text-accent"
                : "bg-surface-hover text-text-muted"
          }`}
        >
          {state === "done" ? (
            <Check size={16} />
          ) : state === "active" ? (
            <span style={{ animation: "spin 1.2s linear infinite", display: "flex" }}>
              <Loader size={16} />
            </span>
          ) : (
            <PhaseIcon size={15} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13.5px] font-semibold text-text-primary">{phase.title}</span>
            {state === "active" && <Pill tone="accent">In progress</Pill>}
            {state === "done" && <Pill tone="muted">Done</Pill>}
          </div>
          <p className="m-0 mt-0.5 text-xs text-text-muted">
            {fillTemplate(phase.desc, vmLabel, regionLabel)}
          </p>
        </div>
        {state === "active" && (
          <div className="w-[120px] shrink-0">
            <div className="h-1 overflow-hidden rounded-sm bg-surface-hover">
              <div
                className="h-full bg-accent transition-[width] duration-[400ms] ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
        <ChevronRight
          size={14}
          className="shrink-0 text-text-muted transition-transform duration-150"
          style={{ transform: expanded ? "rotate(90deg)" : "none" }}
        />
      </button>
      {expanded && (
        <div className="pb-4 pl-[62px] pr-4">
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {phase.steps.map((s, i) => {
              let stepState: "pending" | "active" | "done" = "pending";
              if (state === "done") stepState = "done";
              else if (state === "active") {
                if (i < currentStepIdx) stepState = "done";
                else if (i === currentStepIdx) stepState = "active";
              }
              return (
                <li
                  key={i}
                  className={`flex items-center gap-2.5 text-[12.5px] ${
                    stepState === "pending" ? "text-text-muted" : "text-text-secondary"
                  }`}
                >
                  {stepState === "done" && <Check size={12} className="text-accent" />}
                  {stepState === "active" && (
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-accent"
                      style={{ animation: "status-pulse 1.4s ease-in-out infinite" }}
                    />
                  )}
                  {stepState === "pending" && (
                    <span className="h-1.5 w-1.5 rounded-full bg-border" />
                  )}
                  <span>{fillTemplate(s, vmLabel, regionLabel)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Provisioning screen ─────────────────────────────
export function Provisioning({
  form,
  tenantId,
  agentId,
  onDone,
  onFail,
  onCancel,
}: {
  form: CreateAgentInput;
  tenantId: string;
  agentId: string | null;
  onDone: () => void;
  onFail: (msg: string) => void;
  onCancel: () => void;
}) {
  const vmId = (form.runtimeConfig?.vm_size as string | undefined) ?? "Standard_B2s";
  const regionId = (form.runtimeConfig?.region as string | undefined) ?? "uksouth";
  const vm = VM_SIZES.find((v) => v.id === vmId);
  const region = REGIONS.find((r) => r.id === regionId);
  const vmLabel = vm?.label ?? "VM";
  const regionLabel = region?.label ?? "region";

  const [phaseIdx, setPhaseIdx] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [expanded, setExpanded] = useState<number>(0);
  const [elapsed, setElapsed] = useState(0);
  const [logs, setLogs] = useState<LogLine[]>([
    { ts: "00:00", msg: "Starting deployment…", tone: "ok" },
  ]);
  const [showLogs, setShowLogs] = useState(false);
  const [simDone, setSimDone] = useState(false);

  // Poll real deployment status
  const { data: deployments = [] } = useQuery({
    queryKey: queryKeys.deployments.byCompany(tenantId),
    queryFn: () => azureApi.listDeployments(tenantId),
    refetchInterval: 5000,
    enabled: !!agentId,
  });
  const deployment: AzureDeployment | undefined = agentId
    ? deployments.find((d) => d.agentId === agentId)
    : undefined;
  const realStatus = deployment?.status;
  const realError =
    (deployment?.metadata as { error?: string } | null | undefined)?.error ?? null;

  // Wall clock
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      250,
    );
    return () => clearInterval(id);
  }, []);

  // Simulated phase progression — gives the visual sense of progress.
  useEffect(() => {
    if (simDone) return;
    // Simulated at ~4x real speed; caps at final phase until real status confirms.
    const SPEED = 0.25;
    let p = 0;
    let stepI = 0;
    let localProg = 0;
    const id = setInterval(() => {
      const phase = PHASES[p];
      if (!phase) return;
      localProg += (100 / (phase.duration * SPEED)) * 0.3;
      const phaseStepCount = phase.steps.length;
      const newStepI = Math.min(
        phaseStepCount - 1,
        Math.floor((localProg / 100) * phaseStepCount),
      );
      if (newStepI !== stepI) {
        stepI = newStepI;
        setStepIdx(stepI);
        setLogs((l) => [
          ...l,
          {
            ts: formatTs(new Date()),
            msg: `[${phase.id}] ${fillTemplate(phase.steps[stepI], vmLabel, regionLabel)}`,
          },
        ]);
      }
      setPhaseProgress(Math.min(100, localProg));
      if (localProg >= 100) {
        setLogs((l) => [
          ...l,
          {
            ts: formatTs(new Date()),
            msg: `✓ ${phase.title} complete`,
            tone: "ok",
          },
        ]);
        if (p === PHASES.length - 1) {
          // Hold on final phase — wait for real status to go active.
          localProg = 95;
          return;
        }
        p += 1;
        stepI = 0;
        localProg = 0;
        setPhaseIdx(p);
        setStepIdx(0);
        setExpanded(p);
      }
    }, 300);
    return () => clearInterval(id);
  }, [simDone, vmLabel, regionLabel]);

  // Gate the final transition on real deployment status.
  useEffect(() => {
    if (realStatus === "running" || realStatus === "active") {
      setSimDone(true);
      setPhaseIdx(PHASES.length - 1);
      setPhaseProgress(100);
      setLogs((l) => [
        ...l,
        { ts: formatTs(new Date()), msg: "✓ Agent is live.", tone: "ok" },
      ]);
      const t = setTimeout(() => onDone(), 600);
      return () => clearTimeout(t);
    }
    if (realStatus === "failed") {
      onFail(realError ?? "Deployment failed. Check the agent detail page for details.");
    }
  }, [realStatus, realError, onDone, onFail]);

  const totalDuration = useMemo(
    () => PHASES.reduce((a, p) => a + p.duration, 0),
    [],
  );
  const overallProgress = useMemo(() => {
    const before = PHASES.slice(0, phaseIdx).reduce((a, p) => a + p.duration, 0);
    const cur = (PHASES[phaseIdx]?.duration ?? 0) * (phaseProgress / 100);
    return Math.min(100, Math.round(((before + cur) / totalDuration) * 100));
  }, [phaseIdx, phaseProgress, totalDuration]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  return (
    <div className="mx-auto max-w-[720px] px-6 pt-8 pb-12">
      {/* Hero */}
      <div className="mb-8 text-center">
        <div className="relative mb-4 inline-flex items-center justify-center">
          <div
            className="flex items-center justify-center rounded-[22px] text-white"
            style={{
              width: 88,
              height: 88,
              background: "linear-gradient(135deg, #10a37f 0%, #0d8a6a 100%)",
              boxShadow: "0 8px 24px rgba(16,163,127,0.25)",
            }}
          >
            <Bot size={40} />
          </div>
          <div
            className="absolute rounded-[26px]"
            style={{
              inset: -10,
              border: "2px solid rgba(16,163,127,0.25)",
              animation: "pulseRing 2s ease-out infinite",
            }}
          />
        </div>
        <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.1em] text-text-muted">
          Deploying
        </p>
        <h1 className="m-0 mt-1.5 text-[28px] font-semibold tracking-[-0.015em]">
          Bringing {form.name || "your agent"} online
        </h1>
        <p className="m-0 mt-1.5 text-sm text-text-secondary">
          {vmLabel} · {regionLabel} · usually takes about 2 minutes.
        </p>
      </div>

      {/* Overall progress */}
      <div className="mb-7">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
            {overallProgress}% · {PHASES[phaseIdx]?.title ?? "Done"}
          </span>
          <span className="font-mono text-xs text-text-muted">
            {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")} elapsed
          </span>
        </div>
        <div className="relative h-1.5 overflow-hidden rounded-[3px] bg-surface-hover">
          <div
            className="absolute inset-0 transition-[width] duration-[400ms]"
            style={{
              width: `${overallProgress}%`,
              background: "linear-gradient(90deg, #0d8a6a, #10a37f 60%, #14c493)",
            }}
          />
        </div>
      </div>

      {/* Phases */}
      <div className="mb-5 flex flex-col gap-2">
        {PHASES.map((p, i) => (
          <PhaseRow
            key={p.id}
            phase={p}
            state={i < phaseIdx ? "done" : i === phaseIdx ? "active" : "pending"}
            progress={i === phaseIdx ? phaseProgress : 0}
            currentStepIdx={i === phaseIdx ? stepIdx : 0}
            expanded={expanded === i}
            onToggle={() => setExpanded(expanded === i ? -1 : i)}
            vmLabel={vmLabel}
            regionLabel={regionLabel}
          />
        ))}
      </div>

      {/* Reassurance */}
      <div className="flex items-center gap-3 rounded-[10px] border border-border bg-surface px-4 py-3">
        <Shield size={16} className="shrink-0 text-accent" />
        <div className="flex-1 text-[12.5px] text-text-secondary">
          Safe to close this tab — we'll email you when {form.name || "your agent"} is ready.
        </div>
        <button
          onClick={() => setShowLogs((s) => !s)}
          className="rounded-md border border-border bg-transparent px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-hover"
        >
          {showLogs ? "Hide" : "Show"} raw logs
        </button>
      </div>

      {showLogs && (
        <div className="mt-3">
          <LogStream lines={logs} />
        </div>
      )}

      <div className="mt-6 text-center">
        <button
          onClick={onCancel}
          className="border-0 bg-transparent text-[12.5px] text-text-muted underline"
        >
          Cancel deployment
        </button>
      </div>
    </div>
  );
}

// ── Error screen ────────────────────────────────────
export function ProvisioningError({
  error,
  onRetry,
  onOpen,
}: {
  error: string;
  onRetry: () => void;
  onOpen: () => void;
}) {
  return (
    <div className="mx-auto max-w-[560px] px-6 py-12 text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-[20px] bg-[#ef4444]/10 text-[#ef4444]">
        <AlertCircle size={40} />
      </div>
      <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#ef4444]">
        Deployment failed
      </p>
      <h1 className="m-0 mt-2 text-[26px] font-semibold tracking-[-0.02em]">
        Something went wrong
      </h1>
      <p className="m-0 mt-2.5 text-sm leading-relaxed text-text-secondary">{error}</p>
      <div className="mt-6 flex justify-center gap-2.5">
        <Button variant="outline" size="lg" onClick={onOpen}>
          Open agent page
          <ArrowRight size={14} />
        </Button>
        <Button size="lg" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}

// ── Success / "agent is alive" screen ───────────────
export function AgentAlive({
  form,
  onChat,
  onOpen,
  onBack,
}: {
  form: CreateAgentInput;
  onChat: () => void;
  onOpen: () => void;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto max-w-[560px] px-6 py-12 text-center">
      <div className="relative mx-auto mb-6 h-28 w-28">
        <div
          className="flex h-28 w-28 items-center justify-center rounded-[28px] text-white"
          style={{
            background: "linear-gradient(135deg, #10a37f 0%, #0d8a6a 100%)",
            boxShadow: "0 12px 40px rgba(16,163,127,0.3)",
          }}
        >
          <Bot size={52} />
        </div>
        <div
          className="absolute flex h-[30px] w-[30px] items-center justify-center rounded-full text-white"
          style={{
            bottom: -4,
            right: -4,
            background: "var(--accent)",
            border: "3px solid var(--background)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <Check size={14} strokeWidth={3} />
        </div>
      </div>

      <p className="m-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-accent">
        Agent online
      </p>
      <h1 className="m-0 mt-2 text-[30px] font-semibold tracking-[-0.02em]">
        Say hello to {form.name || "your agent"}
      </h1>
      <p className="m-0 mt-2.5 mb-7 text-[14.5px] leading-relaxed text-text-secondary">
        {form.title ? `${form.title}. ` : ""}Ready to take work. Start with a chat, or open the
        agent's detail page to wire up tools, channels and tasks.
      </p>

      <div className="flex flex-wrap justify-center gap-2.5">
        <Button size="lg" onClick={onChat}>
          <MessageSquare size={15} />
          Start a chat
        </Button>
        <Button variant="outline" size="lg" onClick={onOpen}>
          Open agent page
          <ArrowRight size={14} />
        </Button>
      </div>

      <div className="mt-10 rounded-xl border border-border bg-surface p-5 text-left">
        <p className="m-0 mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          What's next
        </p>
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {[
            { Icon: Key, text: "Connect MCP servers and vault secrets" },
            { Icon: User, text: "Introduce them to your team in #general" },
            { Icon: Wrench, text: "Grant them tools and tune their capabilities" },
          ].map((it, i) => (
            <li key={i} className="flex items-center gap-3 text-[13px] text-text-secondary">
              <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-surface-hover text-text-muted">
                <it.Icon size={14} />
              </div>
              {it.text}
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={onBack}
        className="mt-7 border-0 bg-transparent text-[12.5px] text-text-muted underline"
      >
        Back to agents
      </button>
    </div>
  );
}
